import React, { useEffect, useMemo } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { observable } from "mobx";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
} from "@mui/material";
import PageTitle from "../components/common/pages/PageTitle";
import FormTitle from "../components/common/form/FormTitle";
import FormSmallTextField from "../components/common/form/FormTextField";
import { userStore } from "../../stores/sharedStores";
import { useNotification } from "../../utils/NotificationsContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react";
import CustomTypography from "../components/common/form/CustomTypography";

const paperSx = {
  padding: { xs: 3, sm: 4 },
  mb: 4,
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  background:
    "linear-gradient(180deg, rgba(100,85,237,0.08) 0%, rgba(255,255,255,0) 48%)",
};

const ResetPasswordOtp: React.FC = () => {
  const { showSuccess } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewState = useMemo(
    () =>
      observable({
        showNewPassword: false,
        showConfirmPassword: false,
      }),
    [],
  );


  const recoveryToken = useMemo(
    () => searchParams.get("recoveryToken")?.trim() || "",
    [searchParams],
  );

  const otpTrimmed = userStore.resetOtp.trim();
  const otpValid = otpTrimmed.length === 6;
  const passwordMinLengthValid = userStore.resetNewPassword.length >= 8;
  const passwordsMismatch =
    userStore.resetConfirmPassword.length > 0 &&
    userStore.resetNewPassword !== userStore.resetConfirmPassword;

  const canSubmit =
    !!recoveryToken &&
    otpValid &&
    userStore.resetNewPassword.trim().length > 0 &&
    passwordMinLengthValid &&
    userStore.resetConfirmPassword.trim().length > 0 &&
    userStore.resetNewPassword === userStore.resetConfirmPassword;

  useEffect(() => {
    if (!recoveryToken) navigate("/forget-password");
  }, [recoveryToken, navigate]);

  useEffect(() => {
    userStore.resetResetPasswordForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryToken]);

  useEffect(() => {
    if (userStore.resetErrors.length > 0) userStore.setResetErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userStore.resetOtp,
    userStore.resetNewPassword,
    userStore.resetConfirmPassword,
  ]);

  const handleSubmit = async () => {
    const errors: string[] = [];

    if (!recoveryToken) errors.push("Invalid or expired recovery link.");
    if (!otpTrimmed) errors.push("OTP is required.");
    if (otpTrimmed && otpTrimmed.length !== 6)
      errors.push("OTP must be exactly 6 characters.");
    if (!userStore.resetNewPassword) errors.push("Password is required.");
    if (
      userStore.resetNewPassword &&
      userStore.resetNewPassword.length < 8
    ) {
      errors.push("Password must be at least 8 characters.");
    }
    if (!userStore.resetConfirmPassword)
      errors.push("Confirm Password is required.");
    if (
      userStore.resetNewPassword &&
      userStore.resetConfirmPassword &&
      userStore.resetNewPassword !== userStore.resetConfirmPassword
    ) {
      errors.push("Passwords do not match.");
    }

    if (errors.length > 0) {
      userStore.setResetErrors(errors);
      return;
    }

    try {
      const res = await userStore.confirmResetPassword(
        recoveryToken,
        otpTrimmed,
        userStore.resetNewPassword,
      );

      if (res?.success) {
        showSuccess(res?.message || "Password updated successfully");
        navigate("/login");
      } else {
        userStore.setResetErrors([
          res?.message || "Failed to reset password. Please try again later.",
        ]);
      }
    } catch (err: any) {
      userStore.setResetErrors([
        err?.response?.data?.message ||
        "Failed to reset password. Please try again later.",
      ]);
    }
  };

  const copyPasswordToClipboard = async (password: string) => {
    try {
      await navigator.clipboard.writeText(password);
    } catch (_error) {
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 440 }}>
        <Stack sx={{ mb: 1.5 }}>
          <PageTitle title="Reset password" />
          <CustomTypography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
            Enter the 6-character code from your email, then create a new
            password.
          </CustomTypography>
        </Stack>

        <Paper elevation={0} sx={paperSx}>
          {userStore.resetErrors.length > 0 && (
            <Alert severity="error" variant="outlined" sx={{ mb: 3 }}>
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                {userStore.resetErrors.map((msg, idx) => (
                  <Box component="li" key={`${msg}-${idx}`}>
                    {msg}
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          <Stack spacing={2.5}>
            <Box>
              <FormTitle
                fontSize="12px"
                title="Recovery code"
                marginBottom={1}
              />
              <FormSmallTextField
                label="OTP"
                required
                variant="outlined"
                fontSize="12px"
                value={userStore.resetOtp}
                maxLength={6}
                placeholder="6-character code"
                sx={{ mb: 0 }}
                error={otpTrimmed.length > 0 && !otpValid}
                errorMessage={
                  otpTrimmed.length > 0 && !otpValid
                    ? "OTP must be exactly 6 characters."
                    : ""
                }
                onChange={(_, value) => userStore.setResetOtp(value!)}
              />
            </Box>

            <Divider>
              <CustomTypography variant="caption" color="text.secondary" sx={{ fontSize: "12px" }}>
                New password
              </CustomTypography>
            </Divider>

            <Box>
              <FormTitle fontSize="12px" title=" New Password" marginBottom={1} />
              <FormSmallTextField
                id="reset-new-password"
                label="New password"
                required
                variant="outlined"
                fontSize="12px"
                type={viewState.showNewPassword ? "text" : "password"}
                value={userStore.resetNewPassword}
                maxLength={128}
                sx={{ mb: 0 }}
                error={
                  userStore.resetNewPassword.length > 0 &&
                  !passwordMinLengthValid
                }
                errorMessage={
                  userStore.resetNewPassword.length > 0 &&
                    !passwordMinLengthValid
                    ? "Password must be at least 8 characters."
                    : ""
                }
                onChange={(_, value) => userStore.setResetNewPassword(value!)}
                onKeyDown={(event) => {
                  if (
                    (event.ctrlKey || event.metaKey) &&
                    event.key.toLowerCase() === "c"
                  ) {
                    event.preventDefault();
                    void copyPasswordToClipboard(userStore.resetNewPassword);
                  }
                }}
                endAdornment={
                  <IconButton
                    aria-label={
                      viewState.showNewPassword
                        ? "hide new password"
                        : "show new password"
                    }
                    onClick={() => {
                      viewState.showNewPassword = !viewState.showNewPassword;
                    }}
                    edge="end"
                    size="small"
                    sx={{
                      opacity: 0.55,
                      p: 0.25,
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {viewState.showNewPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                }
              />
            </Box>

            <Box>
              <FormTitle
                fontSize="12px"
                title="Confirm password"
                marginBottom={1}
              />
              <FormSmallTextField
                id="reset-confirm-password"
                label="Confirm password"
                required
                variant="outlined"
                fontSize="12px"
                type={viewState.showConfirmPassword ? "text" : "password"}
                value={userStore.resetConfirmPassword}
                maxLength={128}
                sx={{ mb: 0 }}
                error={passwordsMismatch}
                errorMessage={
                  passwordsMismatch ? "Passwords do not match." : ""
                }
                onChange={(_, value) =>
                  userStore.setResetConfirmPassword(value!)
                }
                endAdornment={
                  <IconButton
                    aria-label={
                      viewState.showConfirmPassword
                        ? "hide confirm password"
                        : "show confirm password"
                    }
                    onClick={() => {
                      viewState.showConfirmPassword =
                        !viewState.showConfirmPassword;
                    }}
                    edge="end"
                    size="small"
                    sx={{
                      opacity: 0.55,
                      p: 0.25,
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {viewState.showConfirmPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                }
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={userStore.resetLoading || !canSubmit}
            >
              {userStore.resetLoading ? "Please wait…" : "Update password"}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default observer(ResetPasswordOtp);
